import "../../Routes/Expired/expired.scss";
import { type FC, useEffect, useState } from "react";
import { CloseDetectionBootstrapper } from "../../_Bootstrap/Bootstrappers/CloseDetectionBootstrapper";
import { getCurrentDatabaseVersion } from "../../Data/DataStores/DataSource";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import {
    deleteOption,
    getOption,
    getTempOption,
    setOption,
} from "../../Utilities/Platform/Options/Options";
import Button from "../Buttons/Button";
import NiceError from "../Errors/NiceError";

export const performRefreshAcrossTabs = async (onStatusUpdate?: (status: string) => void): Promise<void> => {
    setOption("_latest_database_version", "unknown");

    onStatusUpdate?.("Retrieving Latest Database Number");
    await new Promise(res => setTimeout(res, 3000));
    const latestDatabaseVersion = getOption("_latest_database_version");

    onStatusUpdate?.(`Latest Database Number is ${latestDatabaseVersion}`);

    if(latestDatabaseVersion === "unknown") {
        deleteOption("_database_refresh");
        setOption("_database_refresh", "force");
        onStatusUpdate?.("Forcing refresh across all pages");

        return;
    }

    setOption("_database_refresh", latestDatabaseVersion, false);

    if(latestDatabaseVersion === await getCurrentDatabaseVersion()) {
        onStatusUpdate?.("Forcing pages on old version to update");
        await new Promise(res => setTimeout(res, 500));
    }

    await new Promise(res => setTimeout(res, 50));

    onStatusUpdate?.("Cleaning up and updating current page");
    deleteOption("_database_refresh");

    if(typeof window !== "undefined") {
        window.location.reload();
    } else {
        console.error("Unable to reload the page as this doesn't have a window");
    }
};

export const DatabaseBlocked: FC<{ certain: boolean; }> = ({ certain }) => {
    const [ loading, setLoading ] = useState(false);
    const [ supportsRefresh, setSupportsRefresh ] = useState(false);
    const [ status, setStatus ] = useState("");

    useEffect(() => {
        const value = getTempOption("feature:database-blocked-refresh", false);

        if(value !== false) {
            setSupportsRefresh(true);
        }
    }, []);

    const actions = [];

    if(supportsRefresh) {
        actions.push(
            <Button
                key="refresh"
                classes={[ "button-warning" ]}
                disabled={loading}
                onClick={() => {
                    if(loading) {
                        return;
                    }

                    setLoading(true);
                    void performRefreshAcrossTabs(setStatus);
                }}
            >
                {loading && (
                    <div>
                        {status !== "" && (
                            <span className="mr-4">{status}</span>
                        )}
                        <FontAwesomeLight icon="spinner-third" spin />
                    </div>
                )}
                {!loading && "Refresh All Pages"}
            </Button>
        );
    }

    if(!certain) {
        actions.push(
            <Button
                key="close-display"
                classes={[ "button-warning" ]}
                disabled={loading}
                onClick={() => {
                    if(loading) {
                        return;
                    }

                    setLoading(true);

                    CloseDetectionBootstrapper.ignoreForceClose();
                    setOption("_force_close", true);

                    setTimeout(() => {
                        setOption("_force_close", false);

                        window.location.reload();
                    }, 1000);
                }}
            >
                {loading && (
                    <FontAwesomeLight icon="spinner-third" spin />
                )}
                {!loading && "Close Other Windows"}
            </Button>
        );
    }

    return (
        <div id="expired">
            <div className="w-11/12 mx-auto max-w-2xl">
                <img src="/imgs/logo.png" id="shopfront-logo" title="Shopfront" alt="Shopfront" />
                <div id="error-message">
                    <NiceError
                        title="Unable to open the local database"
                        icon="database"
                        actions={actions}
                    >
                        {certain && (
                            <p>This is due to an older version of Shopfront being opened in another tab.</p>
                        )}
                        <p>
                            You&apos;ll need to refresh the page on each tab for Shopfront to start working again,
                            starting with the old versions of Shopfront.
                        </p>
                        {supportsRefresh && (
                            <p>
                                Pressing the button below will attempt to refresh all pages (you may need to manually
                                refresh the pages if it doesn&apos;t work).
                            </p>
                        )}
                        <div className="p-4 w-full bg-amber-600 text-amber-50">
                            <p>
                                Refreshing the pages may result in any unsaved changes being lost, please ensure there
                                are no unsaved changes before continuing.
                            </p>
                        </div>
                        <p>
                            If you see this message returns on refresh, please close all other Shopfront tabs (including
                            the customer display) and press the button again. If you have any issues, please contact
                            Shopfront.
                        </p>
                        {!certain && (
                            <p>
                                If you&apos;re struggling to find the customer display, we can attempt to force it
                                to close (this may also close other Shopfront tabs), if the page still isn&apos;t
                                working, restart your computer.
                            </p>
                        )}
                    </NiceError>
                </div>
            </div>
        </div>
    );
};
